/**
 * src/lib/data/viewer.tsx — active-viewer state + hook (Phase 5.4).
 *
 * The "Who are you?" identity layer. One <ViewerProvider> wraps the
 * app; any descendant reads { viewer, setViewer, refresh, loading,
 * error } via useViewer().
 *
 * Identity model recap (docs/architecture.md §1.6):
 *   - Gate 1: shared view password — entered in PasswordGate, stored
 *     in localStorage["forecast_view_pw"]. Required to talk to the
 *     worker at all.
 *   - Gate 2: soft identity — user picks themselves from the User
 *     list. Picked id stored in localStorage["forecast_active_user_id"].
 *     Sent as X-User-Id on every authenticated request (Phase 5.6
 *     adds server-side enforcement).
 *
 * Boot sequence:
 *   1. Provider mounts → reads stored id + view-password from localStorage
 *   2. If both present → GET /api/users/:id → sets `viewer`
 *   3. If either missing → viewer stays null → consumer shows the picker
 *
 * After the password gate fires (user types correct view-password),
 * ForecastItaly calls `refresh()` to re-attempt the fetch.
 */
import React from "react";
import type { User } from "./types";
// Hotfix (2026-05-16) — single shared worker URL resolver with allowlist
// validation. Rejects stale Pages-domain URLs in localStorage that were
// bricking the picker with "listUsers failed: 404". Replaces the old
// inline readWorkerUrl() below.
import { readWorkerUrl } from "../worker-url";

const STORAGE_KEY = "forecast_active_user_id";
// Phase 6.2 — "Watch as" target. Admin-only. When set, the effective viewer
// (used by the UI) is the target user; the real session identity is held
// separately and is what we audit-attribute back to if writes ever leak
// through. Writes are blocked while watching — see state-storage.ts +
// AdminUsers gating.
const WATCH_KEY = "forecast_watch_as_id";

interface ViewerContextValue {
  /**
   * The EFFECTIVE viewer used by the UI. Equals realViewer unless an admin
   * is watching-as someone else, in which case this is the watch target.
   * Components should read this for "who am I rendering for".
   */
  viewer: User | null;
  /**
   * The actual session identity. Stays stable across watch-as toggles.
   * Used for audit attribution + the "Exit watching" affordance.
   */
  realViewer: User | null;
  /** True iff realViewer is admin and currently impersonating someone else. */
  isWatching: boolean;
  /** True only during the initial fetch. Used by consumers to show a brief loading state. */
  loading: boolean;
  /** Non-null when the last fetch attempt failed (e.g., 401, network). UI can show + retry. */
  error: string | null;
  /**
   * Set the active viewer. Pass a User to mark them as active (also stores
   * the id in localStorage). Pass null to clear (used on sign-out or
   * password reset). Also clears any active watch session.
   */
  setViewer: (user: User | null) => void;
  /**
   * Re-attempt the viewer fetch from localStorage. Call after the view
   * password changes (e.g., right after PasswordGate succeeds) or when
   * the user signs back in.
   */
  refresh: () => Promise<void>;
  /**
   * Admin-only. Start watching another user — the UI re-renders as if
   * they were the viewer (reads only; writes are blocked). Throws if the
   * realViewer isn't admin. Pass the target User.id; the fetch happens
   * inside.
   */
  startWatching: (targetId: string) => Promise<void>;
  /** Exit the watch session. Falls back to realViewer. */
  stopWatching: () => void;
}

const ViewerContext = React.createContext<ViewerContextValue | null>(null);

async function fetchUser(workerUrl: string, viewPassword: string, userId: string): Promise<User | null> {
  const resp = await fetch(`${workerUrl}/api/users/${encodeURIComponent(userId)}`, {
    headers: { "X-View-Password": viewPassword },
  });
  if (resp.status === 404) return null;
  if (!resp.ok) throw new Error(`fetchUser ${resp.status}`);
  return (await resp.json()) as User | null;
}

export function ViewerProvider({ children }: { children: React.ReactNode }) {
  // realViewer is the actual session identity; watchTarget overrides it
  // visually when an admin is impersonating. `viewer` (the effective
  // identity) is a derived value below.
  const [realViewer, setRealViewerState] = React.useState<User | null>(null);
  const [watchTarget, setWatchTargetState] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);

  const doFetch = React.useCallback(async () => {
    if (typeof window === "undefined") {
      setLoading(false);
      return;
    }
    const id = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
    const pw = (() => { try { return localStorage.getItem("forecast_view_pw"); } catch { return null; } })();
    if (!id || !pw) {
      if (mountedRef.current) {
        setRealViewerState(null);
        setWatchTargetState(null);
        setLoading(false);
        setError(null);
      }
      return;
    }
    try {
      const u = await fetchUser(readWorkerUrl(), pw, id);
      if (!mountedRef.current) return;
      if (u && u.active) {
        setRealViewerState(u);
        // Restore an in-flight watch session if the page reloaded.
        // Guard: only honor it if the realViewer is still an admin.
        if (u.isAdmin) {
          try {
            const watchId = localStorage.getItem(WATCH_KEY);
            if (watchId && watchId !== u.id) {
              const target = await fetchUser(readWorkerUrl(), pw, watchId);
              if (mountedRef.current && target && target.active) {
                setWatchTargetState(target);
              } else if (mountedRef.current) {
                // Target was deactivated / removed — drop the watch.
                localStorage.removeItem(WATCH_KEY);
              }
            }
          } catch {/* watch restore is best-effort */}
        } else {
          // Non-admin can never watch.
          try { localStorage.removeItem(WATCH_KEY); } catch {}
        }
      } else {
        // ID points at a missing or deactivated user → clear it.
        setRealViewerState(null);
        setWatchTargetState(null);
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
        try { localStorage.removeItem(WATCH_KEY); } catch {}
      }
      setError(null);
    } catch (e: any) {
      if (!mountedRef.current) return;
      setRealViewerState(null);
      setWatchTargetState(null);
      setError(e?.message || String(e));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Initial fetch on mount.
  React.useEffect(() => { doFetch(); }, [doFetch]);

  const setViewer = React.useCallback((user: User | null) => {
    try {
      if (user) localStorage.setItem(STORAGE_KEY, user.id);
      else localStorage.removeItem(STORAGE_KEY);
      // Switching identity drops any active watch session — the new
      // viewer might not be admin and shouldn't inherit impersonation.
      localStorage.removeItem(WATCH_KEY);
    } catch {}
    setRealViewerState(user);
    setWatchTargetState(null);
    setError(null);
  }, []);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    await doFetch();
  }, [doFetch]);

  const startWatching = React.useCallback(async (targetId: string) => {
    if (!realViewer || !realViewer.isAdmin) {
      throw new Error("Watch-as is admin-only");
    }
    if (targetId === realViewer.id) {
      // No-op: watching yourself is the same as not watching.
      setWatchTargetState(null);
      try { localStorage.removeItem(WATCH_KEY); } catch {}
      return;
    }
    const pw = (() => { try { return localStorage.getItem("forecast_view_pw"); } catch { return null; } })();
    if (!pw) throw new Error("Sign in first");
    const target = await fetchUser(readWorkerUrl(), pw, targetId);
    if (!target || !target.active) throw new Error("Target user not found or deactivated");
    try { localStorage.setItem(WATCH_KEY, target.id); } catch {}
    setWatchTargetState(target);
  }, [realViewer]);

  const stopWatching = React.useCallback(() => {
    try { localStorage.removeItem(WATCH_KEY); } catch {}
    setWatchTargetState(null);
  }, []);

  // Effective viewer = watch target if any, else real.
  const effectiveViewer = watchTarget || realViewer;
  const isWatching = !!watchTarget && !!realViewer && watchTarget.id !== realViewer.id;

  const value: ViewerContextValue = {
    viewer: effectiveViewer,
    realViewer,
    isWatching,
    loading,
    error,
    setViewer,
    refresh,
    startWatching,
    stopWatching,
  };
  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

/**
 * Reads the active-viewer context. Throws if used outside a ViewerProvider —
 * fail loud rather than silently return a placeholder, since downstream
 * components rely on knowing whether identity has loaded.
 */
export function useViewer(): ViewerContextValue {
  const ctx = React.useContext(ViewerContext);
  if (!ctx) {
    throw new Error("useViewer() called outside <ViewerProvider>");
  }
  return ctx;
}
